import { CONFIRM_UPLOAD, CREATE_UPLOAD_URL } from '../graphql';

/**
 * The three-step upload.
 *
 * 1. Ask the API for a signed URL, declaring the file's name, type and size.
 * 2. PUT the bytes straight to Cloud Storage.
 * 3. Tell the API the upload happened, so it attaches the record.
 *
 * The bytes never pass through the API, which is what the old multipart
 * `createFile` mutation did — on a service billed per vCPU-second, a 50 MB
 * upload was 50 MB through a container. It also means the size limit is
 * enforced by storage rather than trusted from the browser.
 */

/** Browsers report an empty type for extensions they do not recognise. */
const FALLBACK_TYPE = 'application/octet-stream';

const requestTicket = async (client, file, target) => {
  const { data } = await client.mutate({
    mutation: CREATE_UPLOAD_URL,
    variables: {
      input: {
        filename: file.name,
        mimetype: file.type || FALLBACK_TYPE,
        sizeBytes: file.size,
        target,
      },
    },
  });
  return data.createUploadUrl;
};

/**
 * Both headers are signed into the URL, so they have to match what was
 * declared exactly — Cloud Storage refuses the PUT otherwise. The range
 * header is what stops a client declaring a small file and sending a large
 * one.
 */
const putBytes = async (doFetch, uploadUrl, file) => {
  const response = await doFetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || FALLBACK_TYPE,
      'x-goog-content-length-range': `1,${file.size}`,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`${file.name} could not be uploaded (${response.status})`);
  }
};

export const uploadFile = async ({
  client, file, target, targetId, desc = '', fetch: doFetch = globalThis.fetch,
}) => {
  const ticket = await requestTicket(client, file, target);

  await putBytes(doFetch, ticket.uploadUrl, file);

  const { data } = await client.mutate({
    mutation: CONFIRM_UPLOAD,
    // The token is opaque and carries the object path; the client never
    // builds one, so it cannot choose where the file lands.
    variables: { input: { uploadToken: ticket.uploadToken, targetId, desc } },
  });
  return data.confirmUpload;
};

/**
 * Sequential rather than concurrent: each file costs a signing call, and a
 * learner attaching several photographs on a phone connection is better
 * served by finishing one at a time than by opening five sockets.
 */
export const uploadFiles = async ({ files, ...rest }) => {
  const uploaded = [];
  // Sequential on purpose — see above. `reduce` over promises would read
  // worse than the loop for the same behaviour.
  // eslint-disable-next-line no-restricted-syntax
  for (const file of files) {
    // eslint-disable-next-line no-await-in-loop
    uploaded.push(await uploadFile({ file, ...rest }));
  }
  return uploaded;
};
